import chunk from 'lodash/chunk';
import React from 'react';
import { Md as CardMD } from '@/card/components/card';
import {
  MostActiveHosts,
  MostActiveOwners,
} from '@/chart/components/most-active-accounts';
import styled from 'styled-components';
import { DashboardPrintChart } from './dashboard-print-chart';
import * as exSidepanelItem from './side-panel-item';

function TopOnlinePlatformsToPrint(props) {
  const { kpiData } = props;
  return (
    <CardMD boxShadowOverride title="Top Online Platforms">
      <exSidepanelItem.OnlinePlatformsSidepanelItem kpiData={kpiData} />
    </CardMD>
  );
}

function RentalCategoriesToPrint(props) {
  const { kpiData } = props;
  return (
    <CardMD boxShadowOverride>
      <exSidepanelItem.SidePanelBody kpiData={kpiData} />
    </CardMD>
  );
}

function MostActiveHostsToPrint(props) {
  const { kpiData } = props;
  return (
    <MostActiveHosts
      key="mostActiveHosts"
      mostActiveAccounts={kpiData.latestKpi.topHosts}
      boxShadowOverride
    />
  );
}

function MostActiveOwnersToPrint(props) {
  const { kpiData } = props;
  return (
    <MostActiveOwners
      key="mostActiveOwners"
      mostActiveAccounts={kpiData.latestKpi.topOwners}
      boxShadowOverride
    />
  );
}

const dataTypeToCard = {
  MOST_ACTIVE_HOSTS: (props) => MostActiveHostsToPrint(props),
  MOST_ACTIVE_OWNERS: (props) => MostActiveOwnersToPrint(props),
  RENTAL_CATEGORIES: (props) => RentalCategoriesToPrint(props),
  TOP_ONLINE_PLATFORMS: (props) => TopOnlinePlatformsToPrint(props),
};

const DashboardPrintWrapper = styled.div`
  .appear-only-print {
    position: absolute;
    top: -2000px;
    left: -2000px;
  }
  @media print {
    html,
    body {
      float: none;
    }

    .break-after {
      display: block;
      page-break-after: always;
      position: relative;
    }

    .appear-only-print {
      display: flex;
      position: unset;
      top: unset;
      left: unset;
    }

    .no-break {
      position: absolute;
    }
  }
`;

const AppearOnlyOnPrint = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 8.5in;
  height: 11in;
  padding: 50px;
  flex-direction: column;
`;

const datasetTypeToEntity = {
  CURRENT_STR_PROPERTIES: 'CHART',
  OCCUPANCY: 'CHART',
  AVG_DAILY_RATE: 'CHART',
  REVENUE: 'CHART',
  TRANSIENT_OCCUPANCY_TAX: 'CHART',
  REGISTRATION_RATE: 'CHART',
  RENTAL_TYPE: 'CHART', // stats about types of rental properties
  RESIDENTIAL_TYPE: 'CHART', // stats about types of residential properties
  ONLINE_PLATFORMS: 'CHART', // stats about platforms such as airbnb
  RENTAL_SIZE: 'CHART',
  RENTAL_STRUCTURE: 'CHART',
  MOST_ACTIVE_HOSTS: 'CARD',
  MOST_ACTIVE_OWNERS: 'CARD',
  TOP_ONLINE_PLATFORMS: 'CARD',
  RENTAL_CATEGORIES: 'CARD',
  STACKED_BOOKED_RATIO: 'CHART',
  ESTIMATED_RENTAL_ACTIVITIES: 'CHART',
};

function WrapPrintStyleEntity(props) {
  return (
    <AppearOnlyOnPrint className="appear-only-print">
      {props.children}
    </AppearOnlyOnPrint>
  );
}

function WrapPageBreak(props) {
  return (
    <>
      <div className="no-break">{props.children}</div>
      <div style={{ padding: 0.1 }} className="break-after"></div>
    </>
  );
}

function getPrintStyledEntities(chunkedEntities) {
  return chunkedEntities.map((entity, i) => (
    <WrapPrintStyleEntity key={i}>{entity}</WrapPrintStyleEntity>
  ));
}

function GroupCards(props) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        width: 650,
        flexDirection: 'row',
      }}
    >
      {props.children}
    </div>
  );
}

function getPageBreakEntities(printStyledEntities) {
  const numChunks = printStyledEntities.length;
  const FIRST_CHARTS_NO_PAGEBREAK = 0;
  const pageBreakCharts = [];
  if (numChunks <= 1) {
    return printStyledEntities;
  }
  pageBreakCharts.push(printStyledEntities[FIRST_CHARTS_NO_PAGEBREAK]);
  for (let i = 1; i < numChunks; i++) {
    pageBreakCharts.push(
      <WrapPageBreak key={i}>{printStyledEntities[i]}</WrapPageBreak>
    );
  }
  return pageBreakCharts;
}

const CHARTS_PER_PAGE = 2;

/**
 * Using a class component b/c the print library needs it to assign ref
 */
export class ChartsToPrint extends React.PureComponent {
  constructor(props) {
    super(props);
    this.kpiData = this.props.kpiData;
    this.wrappedEntities = null;
    this.state = {
      numOfWrappedEntities: 0,
    };
    // list of actual card components
    this.preparedCards = [];
    // list of cards with their grouping to match chart sizes
    this.preparedCharts = [];
  }

  determineCardOrChart() {
    const charts = [];
    const cards = [];
    this.props.entitiesToPrint.forEach((entity) => {
      const entityType = datasetTypeToEntity[entity];
      if (entityType === 'CHART') {
        charts.push(entity);
      } else {
        cards.push(entity);
      }
    });
    this.prepareAndGroupCards(cards);
    this.prepareCharts(charts);
  }

  prepareCharts(charts) {
    this.preparedCharts = charts.map((chartType, i) => (
      <DashboardPrintChart chartType={chartType} key={i} />
    ));
  }

  prepareAndGroupCards(cards) {
    const CARDS_TO_CHARTSIZE = 2;
    // List of Card Components without boxShadow
    const cardComponents = cards.map((card, k) => {
      const Card = dataTypeToCard[card];
      return <Card kpiData={this.kpiData} key={k} />;
    });
    const groupedCardComponents = chunk(cardComponents, CARDS_TO_CHARTSIZE);
    // These cards are now similar to charts layout
    this.preparedCards = groupedCardComponents.map((groupedCards, i) => (
      <GroupCards key={i}>{groupedCards.map((Card) => Card)}</GroupCards>
    ));
  }

  getWrappedEntities() {
    this.determineCardOrChart();
    const preparedEntities = [...this.preparedCharts, ...this.preparedCards];
    const chunkedEntities = chunk(preparedEntities, CHARTS_PER_PAGE);
    const printStyledEntities = getPrintStyledEntities(chunkedEntities);
    const entitiesWithPageBreak = getPageBreakEntities(printStyledEntities);
    this.wrappedEntities = entitiesWithPageBreak;
    return this.wrappedEntities.length === 0 ? null : this.wrappedEntities;
  }

  render() {
    // Getting the wrapped entities is intensive so need to complete this here else
    // re-render will happen before they are ready...this took awhile to figure out *FacePalm*
    const WrappedEntities = this.getWrappedEntities();
    return <DashboardPrintWrapper>{WrappedEntities}</DashboardPrintWrapper>;
  }
}
